import { useCreate, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { FrequencyOfSellType, IOrderDetail, OrderStatusKey } from "interfaces";
import { get, isEmpty } from "lodash";
import {
  AddonProductSection,
  BuyerSection,
  OrderSection,
  ParticipantSection,
  PurchasedProductSection,
  VATSection,
} from "./components";
import Button from "components/button";
import { ChevronDownIcon, ClockIcon, MailIcon, PencilIcon, ArrowUpIcon } from "@heroicons/react/outline";
import { DropdownMenu } from "components/dropdownMenu";
import toast from "react-hot-toast";
import { HistoryLodModal } from "components/historyLogModal";
import { useState } from "react";
import { isHaveActionPermission } from "utills/helpers";

export const OrderDetail: React.FC = () => {
  const [openLogModal, setOpenLogModal] = useState<boolean>(false);
  const { mutate, isLoading: isSendingEmail } = useCreate();

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
    refetch,
  } = useOne<IOrderDetail>({
    resource: "checkout/orders",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const _sendPaymentSuccessEmail = () => {
    if (window.confirm(`Are you sure you want to send email payment successful to this order?`)) {
      mutate({
        resource: `checkout/orders/${orderDetailData?.code}/send-email/payment-success`,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Sent successfully");
          refetch();
        },
      });
    }
  };

  const _sendQRCodeEmail = () => {
    if (window.confirm(`Are you sure you want to send email QR code to this order?`)) {
      mutate({
        resource: `checkout/orders/${orderDetailData?.code}/send-email/qr-code`,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Sent successfully");
          refetch();
        },
      });
    }
  };

  const _syncParticipants = async () => {
    const sale_order_code = get(orderDetailData, "code", "");
    const transaction_id = get(orderDetailData, "transaction.id", "");

    if (!sale_order_code) toast.error("Order code not found");
    if (!transaction_id) toast.error("Transaction ID not found");

    if (sale_order_code && transaction_id) {
      if (window.confirm(`Are you sure you want to sync participants for this order?`)) {
        mutate({
          resource: 'checkout/trigger-complete-sale-order',
          meta: {
            ignoreResourceWrapper: true
          },
          values: {
            sale_order_code,
            transaction_id
          },
        },
        {
          onSuccess: () => {
            toast.success("Sync participants successfully");
            refetch();
          },
        });
      }
    }
  }

  const orderDetailData = data?.data ?? null;

  if (isLoading) {
    return <FullScreenLoading />
  }

  const rate = get(orderDetailData?.rates, "rates.VND", 0);

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Order detail"
        additionalLabel={
          <div className="flex gap-x-2 shrink-0">
            <DropdownMenu
              position="bottom"
              label={
                <Button className="w-fit bg-white rounded-md text-brand-500 font-bold border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85">
                  More actions
                  <ChevronDownIcon className="ml-2 size-4 text-brand-500" />
                </Button>
              }
              items={
                [
                ...(isHaveActionPermission("", "orders", "edit")
                  ? [
                      ...(orderDetailData?.event_frequency_of_sell ===
                        FrequencyOfSellType.ONCE &&
                      orderDetailData?.order_status ===
                        OrderStatusKey.SUCCESSFUL &&
                      orderDetailData.event_type === "attractions"
                        ? [
                            {
                              label: "Resend email QR code",
                              icon: (
                                <MailIcon className="size-4 text-brand-500" />
                              ),
                              onClick: () => _sendQRCodeEmail(),
                            },
                          ]
                        : []),
                      {
                        label: "Edit",
                        icon: <PencilIcon className="size-4 text-brand-500" />,
                        href: `/orders/${orderDetailData?.code}/edit`,
                      },
                    ]
                  : []),
                {
                  label: "Log",
                  icon: <ClockIcon className="size-4 text-brand-500" />,
                  onClick: () => setOpenLogModal(true),
                },
                ...(isHaveActionPermission("", "orders", "edit")
                ? [
                    {
                      label: "Sync participants",
                      icon: <ArrowUpIcon className="size-4 text-brand-500" />,
                      onClick: () => _syncParticipants()
                    },
                  ]
                : []),
              ]}
            />
            {(orderDetailData?.order_status === OrderStatusKey.SUCCESSFUL && isHaveActionPermission("", "orders", "edit")) ? (
              <Button
                onClick={_sendPaymentSuccessEmail}
                className="w-[46px] h-[46px] sm:w-auto sm:h-auto"
              >
                <span className="sr-only">Edit</span>
                <MailIcon className="size-4 text-white" />
                <span className="hidden sm:block sm:ml-2">
                  Resend email Payment Successful
                </span>
              </Button>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="box-container flex items-center justify-between">
          <div className="font-bold text-lg sm:text-xl sm:flex sm:items-center sm:gap-x-4">
            <span className="text-brand-secondary-500">#{orderDetailData?.code}</span>
          </div>
        </div>
      </div>
      {/* BUYER INFORMATION */}
      <BuyerSection orderDetailData={orderDetailData} />

      {/* ORDERR INFORMATION */}
      <OrderSection orderDetailData={orderDetailData} rate={rate} />

      {/* ADDON PRODUCT */}
      <AddonProductSection orderDetailData={orderDetailData} rate={rate} />

      {/* VAT INFORMATION */}
      {!isEmpty(orderDetailData?.tax) ? (
        <VATSection orderDetailData={orderDetailData} />
      ) : <></>}

      <PurchasedProductSection orderDetailData={orderDetailData} rate={rate} />
      <ParticipantSection orderDetailData={orderDetailData} />

      {(isFetching && isPreviousData) || isSendingEmail ? <FullScreenLoading /> : <></>}
      <HistoryLodModal
        resource={`checkout/orders/${orderDetailData?.code}/history`}
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        key="order-detail"
        id="order-detail"
      />
    </div>
  );
}
